import { faBellSlash } from "@fortawesome/pro-regular-svg-icons/faBellSlash";
import { faCircleEllipsis } from "@fortawesome/pro-regular-svg-icons/faCircleEllipsis";
import { faImage } from "@fortawesome/pro-regular-svg-icons/faImage";
import { faShareNodes } from "@fortawesome/pro-regular-svg-icons/faShareNodes";
import { faBell } from "@fortawesome/pro-solid-svg-icons/faBell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { AppImageDimension } from "app/appImages";
import { fetchKnowledgeQuickAccess } from "app/features/menu/menuSlice";
import { store } from "app/store";
import { MprcButton, MprcConfirmDialog, MprcIconButton, MprcIconFa, MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { AsyncUploader } from "components/AsyncUploader";
import { UploadingStatus } from "components/FileUpload/AttachSelection";
import { IconEdit, IconFollow } from "components/icons";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { MprcDropdownBuildGroupedItems, MprcDropdownItem, MprcIconButtonDropdown } from "components/MprcIconButtonDropdown";
import { ImageSelectionResult, MprcImageSelectionDialog } from "components/MprcImageHandler";
import { onImageSelectionUploadError } from "components/MprcImageHandler/MprcImageSelectionDialog";
import { AsyncUploadResult, UserProfileDto } from "generated/CoreApi_v1";
import HtmlUtils from "lib/HtmlUtils";
import MediaUtils from "lib/MediaUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { FoundationPaths } from "routes/FoundationRouter";
import { RootPaths } from "routes/RootRouter";


////////////////////////////////////////////////////////////////////////////////

interface IProps {
  user: UserProfileDto | null;
  onSuccess?: (user?: UserProfileDto) => void;
}

////////////////////////////////////////////////////////////////////////////////

export const ShareUserProfileButton: React.FC<IProps> = (props) => {

  const user = props.user!;

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("knowledge");

  type ShareButtonOptions = "copyLink";

  ///
  const copyLink = () => {
    const host = window.location.protocol + "//" + window.location.host;
    const userProfileUrl = `${host}${RootPaths.BuildUserProfile(user.userId)}`;

    HtmlUtils.copyToClipboard(userProfileUrl)
      .then(() => enqueueSnackbar(t("Feed.Card.Common.CopyLinkSuccessMessage"), { variant: "success" }))
      .catch(() => enqueueSnackbar(t("Feed.Card.Common.CopyLinkFailMessage"), { variant: "error" }));
  };

  ///
  const getMenuItems = (): MprcDropdownItem<ShareButtonOptions>[] => {
    var group1: MprcDropdownItem<ShareButtonOptions>[] = [
      { text: t("Feed.Card.Common.CopyLinkAction"), value: "copyLink" }
    ];

    return MprcDropdownBuildGroupedItems([group1]);
  };

  ///
  const handleMenuItem = (option: ShareButtonOptions) => {
    switch (option) {
      case "copyLink":
        copyLink();
        break;
    }
  };

  return (
    <>
      <MprcFeedTypography variant="action" color="white">
        <MprcIconButtonDropdown
          color="paper"
          icon={<FontAwesomeIcon icon={faShareNodes} fixedWidth />}
          items={getMenuItems()}
          onChange={handleMenuItem}
          separator="blank"
          title={t("Feed.Card.Common.ShareButtonTooltip")}
        />
      </MprcFeedTypography>
    </>
  );
};


////////////////////////////////////////////////////////////////////////////////

export const ActionUserProfileButton: React.FC<IProps> = (props) => {

  const user = props.user!;
  const { allowEditManage, allowEditProfile } = user;

  const { t } = useTranslation("knowledge");
  const navigate = useNavigate();
  const api = useCoreApi();
  const { enqueueSnackbar } = useSnackbar();
  const session = useSession();

  const [posting, setPosting] = useState<boolean>(false);
  const [dialog, setDialog] = useState<any>();
  const [anchor, setAnchor] = React.useState<Element | null>(null);

  type ActionsOptions = "silence" | "block" | "editManage" | "editProfile" | "removeBackgroundImage";
  type MenuItem = { key: ActionsOptions, label: string; };

  const groupOptions: Array<Array<MenuItem>> = [];

  const groupItems1: MenuItem[] = [];
  if (user.silencedOn == null && user.userId != session.user.userId) groupItems1.push({ key: "silence", label: t("Feed.UserProfile.Action.Silence") });
  if (user.blockedOn == null && user.userId != session.user.userId) groupItems1.push({ key: "block", label: t("Feed.UserProfile.Action.Block") });
  if (allowEditManage) groupItems1.push({ key: "editManage", label: t("Feed.UserProfile.Action.EditManage") });
  if (allowEditProfile) groupItems1.push({ key: "editProfile", label: t("Feed.UserProfile.Action.EditProfile") });
  if (user.backgroundImage) groupItems1.push({ key: "removeBackgroundImage", label: t("Feed.UserProfile.Action.RemoveBackgroundImage") });
  if (groupItems1.length > 0) groupOptions.push(groupItems1);

  // Opções do menu
  const menuItems = useMemo(() => {
    var arr: React.ReactNode[] = [];

    groupOptions.forEach((g, gindex) => {
      g.forEach((v) => {
        arr.push(<MenuItem
          key={v.key}
          onClick={
            () => {
              handleMenuItem(v.key);
            }
          }
        >
          {v.label}
        </MenuItem>);
      });

      if (gindex < groupOptions.length - 1)
        arr.push(<Box sx={{ mt: 1.5 }} key={`${gindex}_divider`} />);
    });

    return arr;
  }, [groupOptions]);

  ///
  const removeBackgroundImage = () => {
    setPosting(true);
    api.v1.user.removeBackgroundImage({ userId: user.userId })
      .then((response) => {
        if (response.data) {
          if (props.onSuccess) props.onSuccess();
          enqueueSnackbar(t("Feed.UserProfile.UpdateBackgroundImageMessage"), { variant: "success" });
        }
        else {
          enqueueSnackbar(t("Error.PostFailed", { ns: "shared" }), { variant: "error" });
        }
      })
      .catch((error) => {
        enqueueSnackbar(error, { variant: "error" });
      })
      .finally(() => setPosting(false));
  };

  ///
  const doSilence = () => {
    setPosting(true);
    api.v1.knowledgeSubcription.toggleSilenceProfile({ userId: user.userId })
      .then((response) => {
        if (props.onSuccess) props.onSuccess({ ...user, silencedOn: response.data });
        enqueueSnackbar(t("Feed.UserProfile.SilencedMessage"), { variant: "success" });
      })
      .catch((error) => {
        enqueueSnackbar(error, { variant: "error" });
      })
      .finally(() => setPosting(false));
  };

  ///
  const doBlock = () => {
    setPosting(true);
    api.v1.knowledgeSubcription.toggleBlockProfile({ userId: user.userId })
      .then((response) => {
        if (props.onSuccess) props.onSuccess({ ...user, blockedOn: response.data });
        enqueueSnackbar(t("Feed.UserProfile.Action.BlockedMessage"), { variant: "success" });
      })
      .catch((error) => {
        enqueueSnackbar(error, { variant: "error" });
      })
      .finally(() => setPosting(false));
  };

  ///
  const handleMenuItem = (option: ActionsOptions) => {

    setAnchor(null);

    switch (option) {
      case "silence":
        setDialog(<MprcConfirmDialog
          open
          severity="question"
          message={<span>{t("Feed.UserProfile.SilenceConfirmMessage")}</span>}
          onClose={
            (result) => {
              if (result) {
                doSilence();
              }
              setDialog(null);
            }
          }
        />);
        break;

      case "block":
        setDialog(<MprcConfirmDialog
          open
          severity="question"
          message={<span>{t("Feed.UserProfile.BlockConfirmMessage")}</span>}
          onClose={
            (result) => {
              if (result) {
                doBlock();
              }
              setDialog(null);
            }
          }
        />);
        break;

      case "editManage":
        navigate(FoundationPaths.UserList, {
          state: {
            openForm: {
              userId: user.userId,
              name: user.name,
              formToGo: "edit"
            }
          }
        });
        break;

      case "editProfile":
        navigate(RootPaths.BuildEditProfile(user.userId));
        break;

      case "removeBackgroundImage":
        removeBackgroundImage();
        break;
    }
  };

  return (
    <>
      <MprcRequestIndicator visible={posting} />

      {dialog}

      {
        menuItems.length > 0
          ?
          <MprcFeedTypography variant="action" color="white">
            <IconButton color="inherit" onClick={(e) => setAnchor(e.currentTarget)}>
              <FontAwesomeIcon icon={faCircleEllipsis} fixedWidth />
            </IconButton>
            <Menu
              anchorEl={anchor}
              open={anchor != null}
              onClose={() => setAnchor(null)}
            >
              {menuItems}
            </Menu>
          </MprcFeedTypography>
          : null
      }
    </>
  );
};

////////////////////////////////////////////////////////////////////////////////

export const EditProfileButton: React.FC<{ onBeforeClick?: () => void; }> = ({ onBeforeClick }) => {
  const { t } = useTranslation("knowledge");
  const navigate = useNavigate();

  ///
  const handleClick = () => {
    onBeforeClick && onBeforeClick();
    navigate(RootPaths.EditSelfProfile);
  };

  return (
    <MprcButton
      text={t("Feed.UserProfile.EditProfileButtonText")}
      title={t("Feed.UserProfile.EditProfileButtonTitle")}
      color={"primary"}
      variant="contained"
      startIcon={<IconEdit size="sm" />}
      onClick={handleClick}
    />
  );
};


export const FollowUserProfileButton: React.FC<IProps> = (props) => {

  const { user } = props;

  const api = useCoreApi();
  const { t } = useTranslation("knowledge");
  const { enqueueSnackbar } = useSnackbar();

  const [posting, setPosting] = useState<boolean>(false);
  const [viewError, setViewError] = useState<any>(null);

  ///
  const doSubscribe = async () => {
    setPosting(true);
    try {
      var response = await api.v1.knowledgeSubcription.subscribeUser({ userId: user!.userId });
      if (response.data) {
        enqueueSnackbar(t("Subcriptions.Signup.Author.SubscribeSuccessMessage", { name: user!.name }), { variant: "success" });
        store.dispatch(fetchKnowledgeQuickAccess({}));
        if (props.onSuccess) props.onSuccess();
      }
      else {
        enqueueSnackbar(t("Error.PostFailed", { ns: "shared" }), { variant: "error" });
      }
    }
    catch (e) {
      setViewError(e);
    }
    finally {
      setPosting(false);
    }
  };

  ///
  const handleClick = () => {
    doSubscribe();
  };

  return (
    <>
      <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

      <MprcButton
        text={t("Feed.UserProfile.FollowButtonText")}
        title={t("Feed.UserProfile.FollowButtonTitle")}
        color={"primary"}
        variant="contained"
        disabled={posting}
        startIcon={<IconFollow />}
        onClick={handleClick}
      />
    </>
  );
};

///
export const UnfollowUserProfileButton: React.FC<IProps> = (props) => {

  const { user } = props;

  const api = useCoreApi();
  const { t } = useTranslation("knowledge");
  const { enqueueSnackbar } = useSnackbar();

  const [posting, setPosting] = useState<boolean>(false);
  const [viewError, setViewError] = useState<any>(null);

  ///
  const doSubscribe = async () => {
    setPosting(true);
    try {
      var response = await api.v1.knowledgeSubcription.unsubscribeUser({ userId: user!.userId });
      if (response.data) {
        enqueueSnackbar(t("Subcriptions.Signup.Author.UnsubscribeSuccessMessage", { name: user!.name }), { variant: "success" });
        store.dispatch(fetchKnowledgeQuickAccess({}));
        if (props.onSuccess) props.onSuccess();
      }
      else {
        enqueueSnackbar(t("Error.PostFailed", { ns: "shared" }), { variant: "error" });
      }
    }
    catch (e) {
      setViewError(e);
    }
    finally {
      setPosting(false);
    }
  };

  ///
  const handleClick = () => {
    doSubscribe();
  };

  return (
    <>
      <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

      <MprcButton
        text={t("Feed.UserProfile.UnfollowButtonText")}
        title={t("Feed.UserProfile.UnfollowButtonTitle")}
        color="success"
        variant="contained"
        disabled={posting}
        startIcon={<IconFollow />}
        onClick={handleClick}
      />
    </>
  );
};

export const ToggleMutedUserProfileButton: React.FC<IProps> = (props) => {

  const { user } = props;

  const { t } = useTranslation("knowledge");
  const api = useCoreApi();
  const { enqueueSnackbar } = useSnackbar();

  const [posting, setPosting] = useState<boolean>(false);
  const [viewError, setViewError] = useState<any>(null);

  ///
  const doToggleMuted = async () => {
    setPosting(true);
    try {
      var result = await api.v1.knowledgeSubcription.toggleMutedUserProfile({ userId: user!.userId });

      if (result) {
        if (user!.muted) {
          enqueueSnackbar(t("Feed.UserProfile.MutedMessage"), { variant: "success" });
        }
        else {
          enqueueSnackbar(t("Feed.UserProfile.UnmutedMessage"), { variant: "success" });
        }
      }

      if (props.onSuccess) props.onSuccess();
    }
    catch (e) {
      setViewError(e);
    }
    finally {
      setPosting(false);
    }
  };

  const handleClick = () => {
    doToggleMuted();
  };

  return (
    <>
      <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

      <MprcIconButton
        title={user!.muted ? t("Feed.UserProfile.UnmutedButtonTitle") : t("Feed.UserProfile.MutedButtonTitle")}
        color={user!.muted ? "muted" : "success"}
        disabled={posting}
        onClick={handleClick}
      >
        <MprcIconFa {...props} icon={user!.muted ? faBellSlash : faBell} />
      </MprcIconButton>
    </>
  );
};

export const SilencedUserProfileButton: React.FC<IProps> = (props) => {
  const user = props.user!;

  const { t } = useTranslation("knowledge");
  const api = useCoreApi();
  const { enqueueSnackbar } = useSnackbar();

  const [posting, setPosting] = useState<boolean>(false);
  const [dialog, setDialog] = useState<any>();

  ///
  const doUnsilence = () => {
    setPosting(true);
    api.v1.knowledgeSubcription.toggleSilenceProfile({ userId: user.userId })
      .then(() => {
        if (props.onSuccess) props.onSuccess({ ...user, silencedOn: null });
        enqueueSnackbar(t("Feed.UserProfile.UnsilencedMessage"), { variant: "success" });
      })
      .catch((error) => {
        enqueueSnackbar(error, { variant: "error" });
      })
      .finally(() => setPosting(false));
  };

  ///
  const handleClick = () => {
    setDialog(<MprcConfirmDialog
      open
      severity="question"
      message={<span>{t("Feed.UserProfile.UnsilenceConfirmMessage")}</span>}
      onClose={
        (result) => {
          if (result) {
            doUnsilence();
          }
          setDialog(null);
        }
      }
    />);
  };

  return (
    <>
      {dialog}

      <MprcButton
        text={t("Feed.UserProfile.SilencedButtonText")}
        title={t("Feed.UserProfile.SilencedButtonTitle")}
        color="warning"
        variant="contained"
        disabled={posting}
        startIcon={<IconFollow />}
        onClick={handleClick}
      />
    </>
  );
};

export const BlockedUserProfileButton: React.FC<IProps> = (props) => {
  const user = props.user!;

  const { t } = useTranslation("knowledge");
  const api = useCoreApi();
  const { enqueueSnackbar } = useSnackbar();

  const [posting, setPosting] = useState<boolean>(false);
  const [dialog, setDialog] = useState<any>();

  ///
  const doUnblock = () => {
    setPosting(true);
    api.v1.knowledgeSubcription.toggleBlockProfile({ userId: user.userId })
      .then(() => {
        if (props.onSuccess) props.onSuccess({ ...user, blockedOn: null });
        enqueueSnackbar(t("Feed.UserProfile.UnblockedMessage"), { variant: "success" });
      })
      .catch((error) => {
        enqueueSnackbar(error, { variant: "error" });
      })
      .finally(() => setPosting(false));
  };

  const handleClick = () => {
    setDialog(<MprcConfirmDialog
      open
      severity="question"
      message={<span>{t("Feed.UserProfile.UnblockConfirmMessage")}</span>}
      onClose={
        (result) => {
          if (result) {
            doUnblock();
          }
          setDialog(null);
        }
      }
    />);
  };

  return (
    <>
      {dialog}

      <MprcButton
        text={t("Feed.UserProfile.BlockedButtonText")}
        title={t("Feed.UserProfile.BlockedButtonTitle")}
        color="error"
        variant="contained"
        disabled={posting}
        startIcon={<IconFollow />}
        onClick={handleClick}
      />
    </>

  );
};

export const BackgroundImageButton: React.FC<IProps> = (props) => {

  const user = props.user!;

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("knowledge");
  const api = useCoreApi();

  const [dialog, setDialog] = useState<any>();
  const [posting, setPosting] = useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<UploadingStatus>("none");

  ///
  const loadAttachStarted = () => {
    setUploadStatus("uploading");
  };

  ///
  const loadAttachError = (instance: AsyncUploader, thrown: any) => {
    setUploadStatus("canceled");
    onImageSelectionUploadError(instance, thrown, t);
  };

  ///
  const postData = (data: AsyncUploadResult) => {
    setPosting(true);
    api.v1.user.updateBackgroundImage({ userId: user.userId, backgroundImageId: data.mainFile.fileId })
      .then((response) => {
        if (response.data) {
          if (props.onSuccess) props.onSuccess();
          enqueueSnackbar(t("Feed.UserProfile.UpdateBackgroundImageMessage"), { variant: "success" });
        }
        else {
          enqueueSnackbar(t("Error.PostFailed", { ns: "shared" }), { variant: "error" });
        }
      })
      .catch((error) => {
        enqueueSnackbar(error, { variant: "error" });
      })
      .finally(() => setPosting(false));
  };

  ///
  const loadAttachSuccess = (data: AsyncUploadResult) => {
    if (data.error) {
      enqueueSnackbar(t("AsyncUpload.UploadProccessFailed", { ns: "shared" }), { variant: "error" });
    }
    else {
      postData(data);
    }
    setUploadStatus("uploaded");
  };

  ///
  const asyncUploader = useRef<AsyncUploader>(new AsyncUploader({
    onFileUploadSuccess: loadAttachSuccess,
    onFileUploadError: loadAttachError,
    onFileStarted: loadAttachStarted,
  }));

  ///
  const doLoadImage = (image: ImageSelectionResult | null) => {
    if (image != null) {
      var finalImage = image.editedImage ?? image.source;
      asyncUploader.current.uploadImage(finalImage, true);
    }
    setDialog(null);
  };

  ///
  const handleImageSelection = () => {
    setDialog(
      <MprcImageSelectionDialog
        acceptedFiles={MediaUtils.DropzoneAcceptImages()}
        initialSource="library"
        backgroundType="solid"
        adjustImageDimensionToRatio
        dimension={AppImageDimension.UserBackground}
        allowEdit={false}
        onClose={doLoadImage}
      />
    );
  };

  return (
    <>
      {dialog}

      <MprcRequestIndicator visible={posting || uploadStatus == "uploading"} />

      <MprcIconButton
        title={t("Feed.UserProfile.CustomizeBackgroundTitle")}
        color="paper"
        disabled={posting || uploadStatus == "uploading"}
        onClick={handleImageSelection}
      >
        <MprcIconFa {...props} icon={faImage} />
      </MprcIconButton>
    </>
  );
};
